/**
 * SignatureField Styles
 *
 * Task: T-P5-D4-F2-3
 * Feature: P5-D4-F2 Shared Form Contract Upgrade
 *
 * Canvas-based signature capture with clear button and placeholder.
 * Uses design system CSS variables from globals.css.
 */

/* ═══════════════════════════════════════════════════════════
   CANVAS CONTAINER
   ═══════════════════════════════════════════════════════════ */

.canvasContainer {
  position: relative;
  width: 100%;
  border: 2px solid var(--color-bg-secondary);
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  overflow: hidden;
  transition: border-color 0.2s var(--ease-out);
}

.canvasContainer:hover {
  border-color: var(--color-text-muted);
}

.canvasContainer.hasError {
  border-color: var(--color-error);
  box-shadow: 0 0 0 4px rgba(242, 139, 130, 0.15);
}

/* ═══════════════════════════════════════════════════════════
   CANVAS
   ═══════════════════════════════════════════════════════════ */

.canvas {
  display: block;
  width: 100%;
  height: 150px;
  cursor: crosshair;
}

/* ═══════════════════════════════════════════════════════════
   PLACEHOLDER OVERLAY
   ═══════════════════════════════════════════════════════════ */

.placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  font-size: 14px;
  font-family: var(--font-body);
  pointer-events: none;
}

/* ═══════════════════════════════════════════════════════════
   CLEAR BUTTON
   ═══════════════════════════════════════════════════════════ */

.clearButton {
  position: absolute;
  top: var(--space-2);
  right: var(--space-2);
  min-width: 44px;
  min-height: 32px;
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-bg-secondary);
  border-radius: var(--radius-sm);
  background: var(--color-bg-elevated);
  color: var(--color-text-muted);
  font-size: 12px;
  font-family: var(--font-body);
  cursor: pointer;
  transition: all 0.15s var(--ease-out);
}

.clearButton:hover {
  color: var(--color-error);
  border-color: var(--color-error);
}

/* ═══════════════════════════════════════════════════════════
   SIZE WARNING
   ═══════════════════════════════════════════════════════════ */

.sizeWarning {
  font-size: 12px;
  color: var(--color-error);
  margin-top: var(--space-1);
}

/* ═══════════════════════════════════════════════════════════
   CWR 2.5 — clientBooking signature restyle. Scoped to the booking-form
   root (FormRenderer's data-form-appearance marker) so admin/legacy forms
   are byte-for-byte untouched, matching the fields.module.css discipline.
     · --sig-ink → light token: SignatureField.tsx reads it at stroke time
       (inherited onto the canvas) so the signature draws WHITE on the dark inset.
     · focus → the SAME accent ring the inputs use, replacing the white UA outline.
   ═══════════════════════════════════════════════════════════ */
:global([data-form-appearance='clientBooking']) .canvasContainer {
  --sig-ink: var(--text-primary);
  background: var(--bg-inset);
  border-color: var(--text-muted);
}
/* Kill the iOS touch-drag selection highlight (the "white outline") + any UA focus
   ring; the canvas carries tabIndex=0 (SignatureField.tsx) so a tap focuses it and
   the container picks up the SAME accent ring the inputs use. */
:global([data-form-appearance='clientBooking']) .canvas {
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}
:global([data-form-appearance='clientBooking']) .canvas:focus,
:global([data-form-appearance='clientBooking']) .canvas:focus-visible {
  outline: none;
}
:global([data-form-appearance='clientBooking']) .canvasContainer:focus-within {
  border-color: rgba(var(--accent-rgb), 0.55);
  box-shadow: 0 0 18px rgba(var(--accent-rgb), 0.14);
}
